import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faMoon, faSun } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { animated, useSpring, config } from "react-spring";

export interface DSToggleProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type"
> {
  checked?: boolean;
  showIcons?: boolean;
  onIcon?: IconProp;
  offIcon?: IconProp;
  /** Accessible label for screen readers (required if no visible label) */
  "aria-label"?: string;
  dataTestId: string;
  ref?: React.Ref<HTMLInputElement>;
}

export const DSToggle = ({
  checked = false,
  showIcons = false,
  onIcon = faMoon,
  offIcon = faSun,
  disabled = false,
  dataTestId,
  ref,
  ...props
}: Omit<DSToggleProps, "className">) => {
  // Spring animation for knob movement
  const knobAnimation = useSpring({
    config: config.stiff,
    transform: checked ? "translateX(24px)" : "translateX(0px)",
  });

  // Track background styles
  const trackBaseStyles =
    "absolute left-[5px] right-0 h-full rounded-full transition-colors duration-200 shrink-0";

  const trackStateStyles = disabled
    ? "bg-disabled"
    : checked
      ? "bg-brand shadow-[inset_0px_0px_4px_0px_var(--color-indigo-600)]"
      : "bg-secondary shadow-[inset_0px_0px_4px_0px_var(--shadow-focus)]";

  // Knob styles (position handled by animation)
  const knobBaseStyles =
    "absolute top-[3px] left-[9px] w-6 h-6 bg-white rounded-full shadow-[0px_0px_10px_0px_var(--shadow-primary)]";

  return (
    <label
      data-state={checked ? "on" : "off"}
      data-testid={dataTestId}
      className={`relative w-15 h-[30px] inline-flex items-center ${
        disabled ? "cursor-not-allowed" : "cursor-pointer"
      }`}
    >
      <input
        ref={ref}
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        aria-checked={checked}
        aria-disabled={disabled}
        data-intercom-target={dataTestId}
        className="sr-only peer"
        {...props}
      />

      {/* Track background */}
      <div
        className={`${trackBaseStyles} ${trackStateStyles}`}
        aria-hidden="true"
      />

      {/* Knob */}
      <animated.div
        style={knobAnimation}
        className={`${knobBaseStyles} ${
          disabled ? "opacity-50" : ""
        }`}
        aria-hidden="true"
      />

      {/* Icons (optional) */}
      {showIcons && !disabled && (
        <>
          {/* Off icon (left side) */}
          <div
            className="absolute left-[-20px] top-1/2 -translate-y-1/2 text-secondary pointer-events-none"
            aria-hidden="true"
          >
            <FontAwesomeIcon icon={offIcon} className="text-base" />
          </div>

          {/* On icon (right side) */}
          <div
            className="absolute right-[-24px] top-1/2 -translate-y-1/2 text-secondary pointer-events-none"
            aria-hidden="true"
          >
            <FontAwesomeIcon icon={onIcon} className="text-base" />
          </div>
        </>
      )}
    </label>
  );
};

DSToggle.displayName = "DSToggle";
